import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useAccount, useSnapshots } from '~/apis';
import { useTranslation } from '~/app';
import { buildDebugTrainingSnapshotRoute, ROUTE_SNAPSHOTS } from '~/app/routes';
import Link from '~/components/Link';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import { startDebug } from '~/game';
import { router } from '~/router';
import type { SnapshotListItem } from '~/server/src/dtos';
import { persistDebugSpawnSource } from '~/setup/debugSpawnStorage';
import { buildSnapshotTrainingSlug, isDebugGameTrainingId } from '~/snapshots/snapshotTrainingLoad';
import { cn } from '~/utils/styles';

const COMMON_LIST_PAGE_SIZE = 50;

const CommonSnapshotsPanel = ({
  className,
  onSnapshotStart,
}: {
  className?: string;
  /** Called when a common snapshot is started (e.g. to close the debug panel). */
  onSnapshotStart?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: account } = useAccount();

  const { data, isLoading, isError } = useSnapshots({
    page: 1,
    pageSize: COMMON_LIST_PAGE_SIZE,
    source: 'common',
    sortBy: 'message',
    sortDir: 'asc',
  });

  const startFreeGame = useCallback(
    (item: SnapshotListItem) => {
      onSnapshotStart?.();
      persistDebugSpawnSource('common', account?.userId);
      dispatch(
        startDebug({
          gameName: item.commonGameName,
          playerCount: item.commonPlayerCount,
          horseCount: item.commonHorseCount,
          spawnSource: 'common',
        }),
      );
      router.navigate({ to: '/freegame' });
    },
    [account?.userId, dispatch, onSnapshotStart],
  );

  const startTraining = useCallback(
    (item: SnapshotListItem) => {
      const gameName = item.commonGameName ?? item.snapshotId ?? item.id;
      onSnapshotStart?.();
      router.navigate({
        to: buildDebugTrainingSnapshotRoute(buildSnapshotTrainingSlug(gameName, 0)),
      });
    },
    [onSnapshotStart],
  );

  const items = data?.items ?? [];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {isLoading && (
        <div className="flex justify-center py-4">
          <LoadingSpinner size="sm" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-600">{t('debug_common_snapshots_load_error')}</p>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-gray-600">{t('debug_search_no_server_results')}</p>
      )}
      {!isLoading && items.length > 0 && (
        <ul className="grid max-h-[min(40vh,16rem)] gap-1 overflow-y-auto sm:grid-cols-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="rounded px-1 py-0.5 text-sm text-[#405820] hover:bg-[#405820]/5"
            >
              <span className="line-clamp-2">{item.message}</span>
              <span className="mt-0.5 block text-xs text-[#405820]/70">{item.snapshotType}</span>
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                <Link
                  underline="hover"
                  className="cursor-pointer text-xs font-medium text-[#405820]"
                  onClick={() => startFreeGame(item)}
                >
                  {t('debug_snapshot_load_free')}
                </Link>
                {(item.commonGameName ?? item.snapshotId ?? item.id) &&
                  isDebugGameTrainingId(
                    item.commonGameName ?? item.snapshotId ?? item.id,
                  ) && (
                    <Link
                      underline="hover"
                      className="cursor-pointer text-xs font-medium text-[#405820]"
                      onClick={() => startTraining(item)}
                    >
                      {t('debug_snapshot_load_training')}
                    </Link>
                  )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Link
        underline="hover"
        className="cursor-pointer text-sm font-medium text-[#405820]"
        onClick={() => router.navigate({ to: ROUTE_SNAPSHOTS })}
      >
        {t('snapshots_quick_menu_open_all')}
      </Link>
    </div>
  );
};

export default CommonSnapshotsPanel;
